/**
 * ProfitCelebration — конфетти + тост когда какой-то роут закрылся с прибылью.
 *
 * Источник: polls /api/notifications каждые 5с (тот же endpoint что bell).
 * Чтобы не палить конфетти при reload'е страницы, последний показанный ts
 * сохраняется в localStorage (`sb_celebration_last_ts`).
 *
 * Срабатывает только при PnL > 0 — отрицательные исходы тихо логируются
 * в bell. Любители грустного звука могут поставить sb_celebration_loss=1.
 *
 * Конфетти — чистый canvas без npm-зависимостей, 60 частиц на ~2.5с.
 */
import React, { useEffect, useRef, useState } from 'react';
import { sharedGet, NOTIFICATIONS_URL } from '../utils/sharedGet';
import { usePolling } from '../utils/poll';
import { useTranslation } from 'react-i18next';

interface NotificationItem {
  ts: number;
  route_id: number;
  kind: string;
  data?: Record<string, any>;
}

const CLOSE_KINDS = new Set(['paper_close_filled', 'live_close_filled', 'take_profit_triggered']);

interface Burst {
  id: number;
  pnl: number;
  routeId: number;
}

const Confetti: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = window.innerWidth, h = window.innerHeight;
    c.width = w * dpr; c.height = h * dpr;
    c.style.width = `${w}px`; c.style.height = `${h}px`;
    ctx.scale(dpr, dpr);

    const colors = ['#10b981', '#22c55e', '#84cc16', '#eab308', '#f59e0b', '#3b82f6', '#a855f7'];
    type P = { x: number; y: number; vx: number; vy: number; rot: number; vr: number; size: number; color: string; shape: 0|1 };
    const particles: P[] = [];
    // Стреляем из левого и правого нижних углов одновременно — fountain pattern,
    // более «event-like» чем равномерный rain.
    for (let i = 0; i < 60; i++) {
      const fromLeft = i % 2 === 0;
      const baseAngle = fromLeft ? -Math.PI/3 : -2*Math.PI/3;
      const ang = baseAngle + (Math.random() - 0.5) * 0.7;
      const speed = 14 + Math.random() * 10;
      particles.push({
        x: fromLeft ? 10 : w - 10,
        y: h - 10,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.3,
        size: 6 + Math.random() * 6,
        color: colors[(Math.random() * colors.length) | 0],
        shape: Math.random() > 0.5 ? 0 : 1,
      });
    }

    let raf = 0;
    let t0 = performance.now();
    const draw = (t: number) => {
      const dt = Math.min(40, t - t0) / 16;
      t0 = t;
      ctx.clearRect(0, 0, w, h);
      let alive = 0;
      for (const p of particles) {
        p.vy += 0.35 * dt;        // gravity
        p.vx *= Math.pow(0.99, dt);
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        if (p.y < h + 30) {
          alive++;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          if (p.shape === 0) {
            ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          } else {
            ctx.beginPath();
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
      }
      if (alive > 0) {
        raf = requestAnimationFrame(draw);
      } else {
        onDone();
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);
  return (
    <canvas ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[110]"/>
  );
};

const Banner: React.FC<{ burst: Burst; onDone: () => void }> = ({ burst, onDone }) => {
  const { t } = useTranslation('profitCelebration');
  useEffect(() => {
    const t = window.setTimeout(onDone, 4000);
    return () => window.clearTimeout(t);
  }, [onDone]);
  const fmt$ = (n: number) => `${n < 0 ? '-' : '+'}$${Math.abs(n).toFixed(2)}`;
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[111] pointer-events-none
                    animate-sb-fade-zoom-in">
      <div className="rounded-xl border border-trade-green/50 bg-popover/95 backdrop-blur px-5 py-3
                      shadow-2xl shadow-trade-green/20">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🎉</span>
          <div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{t('route', { id: burst.routeId })}</div>
            <div className="text-lg font-bold text-trade-green leading-tight">
              {fmt$(burst.pnl)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProfitCelebration: React.FC = () => {
  const [burst, setBurst] = useState<Burst | null>(null);
  const [confettiKey, setConfettiKey] = useState(0);
  const lastSeenRef = useRef<number>(0);

  // Загружаем last_seen из localStorage один раз при mount, иначе reload =
  // всегда показывать конфетти для последнего close-event, что раздражает.
  useEffect(() => {
    try {
      lastSeenRef.current = Number(localStorage.getItem('sb_celebration_last_ts') || 0);
    } catch {/* */}
  }, []);

  // Та же ручка, что у колокольчика, поэтому опрос редкий и с защитой от
  // наложения: закрытие сделки — событие раз в час, а не раз в семь секунд, и
  // ради него нельзя занимать очередь к бэкенду, в которой стоит история
  // графика. Конфетти, опоздавшее на несколько секунд, никто не заметит.
  usePolling(async () => {
      try {
        // TTL 20 с: колокольчик просит тот же журнал раз в 15 с — берём его
        // ответ, а не шлём свой запрос.
        const r: any = await sharedGet(NOTIFICATIONS_URL, 20_000);
        const items: NotificationItem[] = r?.items || [];
        // Бэк отдаёт newest-first; идём сначала-в-конец чтобы выбрать самый
        // ранний неувиденный close с прибылью (обычно один, но на всякий).
        const fresh = items
          .filter(it => CLOSE_KINDS.has(it.kind))
          .filter(it => (it.ts || 0) > lastSeenRef.current)
          .reverse();
        if (!fresh.length) return;
        // ВАЖНО: даже если PnL <= 0 — двигаем watermark, иначе будем
        // перепроверять одни и те же события в каждом тике.
        const maxTs = Math.max(...fresh.map(f => f.ts || 0));
        lastSeenRef.current = maxTs;
        try { localStorage.setItem('sb_celebration_last_ts', String(maxTs)); } catch {/* */}

        const profitable = fresh.filter(f => Number((f.data || {}).pnl_usd ?? 0) > 0);
        if (!profitable.length) return;
        // Берём самый прибыльный за пачку — даже если их два пришло в одном poll'е,
        // лучше зацелебрейтить лучший, а не самый недавний.
        const best = profitable.reduce((a, b) =>
          Number((b.data || {}).pnl_usd ?? 0) > Number((a.data || {}).pnl_usd ?? 0) ? b : a);
        setBurst({
          id: Date.now(),
          pnl: Number((best.data || {}).pnl_usd ?? 0),
          routeId: best.route_id,
        });
        setConfettiKey(k => k + 1);
      } catch {/* unauth / offline — silent */}
    // initialDelayMs: не палим сразу при mount, даём ws-у и авторизации
    // проснуться, а терминалу — забрать свою историю без очереди.
  }, [], { intervalMs: 30000, initialDelayMs: 5000 });

  return (
    <>
      {burst && <Confetti key={confettiKey} onDone={() => {/* keep banner a bit longer */}} />}
      {burst && <Banner burst={burst} onDone={() => setBurst(null)} />}
    </>
  );
};

export default ProfitCelebration;
